// Welcome Bonus — client hook (status + claim).

import { useCallback, useEffect, useRef, useState } from 'react';
import { BALANCE_REFRESH_EVENT } from '@/utils/balanceRefresh';
import {
    claimSignupBonus,
    fetchSignupBonus,
    DAILY_BUDGET_USER_MESSAGE,
    type SignupBonus,
} from './signupBonusService';

export function useSignupBonus() {
    const [bonus, setBonus] = useState<SignupBonus | null>(null);
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(false);
    const [claimError, setClaimError] = useState<string | null>(null);
    const prevStatusRef = useRef<SignupBonus['status'] | null>(null);

    const load = useCallback(async (forceRefresh = false) => {
        try {
            const data = await fetchSignupBonus(forceRefresh);
            const prev = prevStatusRef.current;
            // getPlayerStatus may expire+recall during this GET — refresh wallet when
            // status settles away from active (or first paint already expired).
            const shouldRefreshBalance =
                (prev === 'active' && (data?.status === 'expired' || data?.status === 'completed' || data?.status === 'in_review'))
                || (prev === 'in_review' && data?.status === 'completed')
                || (prev == null && data?.status === 'expired');
            prevStatusRef.current = data?.status ?? null;
            setBonus(data);
            if (data?.status === 'inactive' && data.claimBlockCode === 'DAILY_BUDGET') {
                setClaimError(data.claimBlockMessage || DAILY_BUDGET_USER_MESSAGE);
            } else if (data?.status !== 'inactive') {
                setClaimError(null);
            }
            if (shouldRefreshBalance) {
                window.dispatchEvent(new Event(BALANCE_REFRESH_EVENT));
            }
        } catch {
            setBonus(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const claim = useCallback(async () => {
        setClaiming(true);
        setClaimError(null);
        try {
            const data = await claimSignupBonus();
            prevStatusRef.current = data?.status ?? null;
            setBonus(data);
            window.dispatchEvent(new Event(BALANCE_REFRESH_EVENT));
            return data;
        } catch (err: unknown) {
            const e = err as {
                message?: string;
                error?: string | { message?: string; code?: string };
                data?: { error?: { message?: string; code?: string }; message?: string };
            };
            const code =
                (typeof e.error === 'object' && e.error?.code)
                || e.data?.error?.code;
            if (code === 'DAILY_BUDGET') {
                setClaimError(DAILY_BUDGET_USER_MESSAGE);
                throw err;
            }
            const nested =
                (typeof e.error === 'object' && e.error?.message)
                || e.data?.error?.message
                || e.data?.message;
            const msg = nested || e.message || 'Unable to activate welcome bonus. Please try again later.';
            setClaimError(msg);
            throw err;
        } finally {
            setClaiming(false);
        }
    }, []);

    useEffect(() => {
        load(true);
        const onRefresh = () => load(true);
        window.addEventListener(BALANCE_REFRESH_EVENT, onRefresh);
        return () => window.removeEventListener(BALANCE_REFRESH_EVENT, onRefresh);
    }, [load]);

    return { bonus, loading, claiming, claimError, setClaimError, claim, reload: () => load(true) };
}
